import React, { useEffect, useState } from 'react';
import { Inbox, Check, X, ArrowLeftRight, GitMerge } from 'lucide-react';
import {
  listPendentes,
  promotePendente,
  isSuspenseAccount,
  PendentesResponse,
  PendenteEntry,
} from '../services/pendentesService';
import { getDeclaredAccounts, linkTransfer } from '../services/apiService';

function fmtBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const NATURE_COLORS: Record<string, string> = {
  Receitas: 'bg-green-100 text-green-800',
  Despesas: 'bg-red-100 text-red-800',
  Ativos: 'bg-blue-100 text-blue-800',
  Passivo: 'bg-purple-100 text-purple-800',
};

export default function PendentesPage() {
  const [data, setData] = useState<PendentesResponse | null>(null);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [promoting, setPromoting] = useState(false);
  const [ingestSummary, setIngestSummary] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState(false);
  const [reasonFilter, setReasonFilter] = useState<string>('all');

  const reload = () => {
    setLoading(true);
    Promise.all([listPendentes(), getDeclaredAccounts()])
      .then(([p, a]) => {
        setData(p);
        setAccounts(a.accounts.filter((x) => !x.endsWith(':Pendente')));
      })
      .catch(() => setError('Erro ao carregar pendentes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Pega resumo de ingestão recente (vindo do /importar OFX)
    const summary = sessionStorage.getItem('lastIngestSummary');
    if (summary) {
      setIngestSummary(summary);
      sessionStorage.removeItem('lastIngestSummary');
      // Auto-dismiss após 10s
      setTimeout(() => setIngestSummary(''), 10000);
    }
  }, []);

  const openEdit = (xact: PendenteEntry) => {
    setEditing(xact.id);
    // Pre-fill replacements with empty strings for each suspense posting
    const init: Record<string, string> = {};
    for (const p of xact.postings) {
      if (isSuspenseAccount(p.account)) init[p.account] = '';
    }
    setReplacements(init);
  };

  const cancelEdit = () => {
    setEditing(null);
    setReplacements({});
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const linkPair = async (idA: string, idB: string) => {
    setLinking(true);
    setError('');
    try {
      await linkTransfer({ txn_id_a: idA, txn_id_b: idB });
      reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Falha ao vincular como transferência.');
      reload();
    } finally {
      setLinking(false);
    }
  };

  const handleLinkTransfer = async () => {
    if (selected.size !== 2) return;
    const [a, b] = Array.from(selected);
    if (!window.confirm(
      'Vincular os dois pendentes selecionados como UMA transferência? ' +
      'O lançamento duplicado é removido e os fingerprints do banco são ' +
      'preservados (a sincronização do banco-mcp continua funcionando).'
    )) return;
    setSelected(new Set());
    await linkPair(a, b);
  };

  const submitPromote = async (xactId: string) => {
    const filled = Object.fromEntries(
      Object.entries(replacements).filter(([, v]) => v.trim()),
    );
    if (Object.keys(filled).length === 0) {
      setError('Escolha pelo menos uma conta para substituir');
      return;
    }
    setPromoting(true);
    setError('');
    try {
      await promotePendente(xactId, filled);
      cancelEdit();
      reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao promover');
    } finally {
      setPromoting(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Carregando…</div>;
  if (!data) return null;

  const reasonValues = Array.from(
    new Set(data.pendentes.flatMap((x) => (x.tags['pending-reason'] ? [x.tags['pending-reason']] : [])))
  ).sort();
  const filteredPendentes = reasonFilter === 'all'
    ? data.pendentes
    : data.pendentes.filter((x) => x.tags['pending-reason'] === reasonFilter);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Inbox className="h-6 w-6 text-amber-600" />
          Lançamentos Pendentes
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {data.total} transação{data.total === 1 ? '' : 'ões'} aguardando classificação
        </p>
      </div>

      {ingestSummary && (
        <div className="px-4 py-3 bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded flex items-center justify-between">
          <span><Check className="inline h-4 w-4 mr-1" /> {ingestSummary}</span>
          <button onClick={() => setIngestSummary('')} className="text-blue-500 hover:text-blue-700">×</button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(['Receitas', 'Despesas', 'Ativos', 'Passivo'] as const).map((nat) => (
          <div key={nat} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${NATURE_COLORS[nat]}`}>
              {nat}:Pendente
            </div>
            <div className="mt-2 text-2xl font-mono text-gray-900">
              {fmtBRL(Math.abs(data.summary[nat]))}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {data.pendentes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-400">
          Nada pendente. Quando importações ou cargas trouxerem itens sem conta
          definida, eles vão aparecer aqui.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Motivo:</span>
            <select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-700"
            >
              <option value="all">Todos</option>
              {reasonValues.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {reasonFilter !== 'all' && (
              <span className="text-xs text-gray-400">
                ({filteredPendentes.length} de {data.pendentes.length})
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-4 py-2 text-left w-28">Data</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-left">Postings</th>
                <th className="px-4 py-2 text-right w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPendentes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    Nenhum resultado para o filtro selecionado.
                  </td>
                </tr>
              ) : filteredPendentes.map((xact) => {
                const transferPartnerId = xact.tags['transfer-candidate-id'] || null;
                const transferPartner = transferPartnerId
                  ? (data.pendentes.find((p) => p.id === transferPartnerId) ?? null)
                  : null;
                return (
                <React.Fragment key={xact.id}>
                  <tr className={`hover:bg-amber-50/40 ${selected.has(xact.id) ? 'bg-indigo-50' : ''}`}>
                    <td className="px-3 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={selected.has(xact.id)}
                        onChange={() => toggleSelect(xact.id)}
                        className="rounded mt-0.5"
                        title="Selecionar para vincular como transferência"
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-700 align-top">
                      {xact.date.replace(/\//g, '-')}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-gray-900">{xact.payee}</div>
                      {Object.entries(xact.tags).filter(([k]) => k !== 'status').slice(0, 4).map(([k, v]) => {
                        const isNeedsMerge = k === 'pending-reason' && v === 'needs-merge';
                        return (
                          <span
                            key={k}
                            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs mr-1 mt-1 ${
                              isNeedsMerge
                                ? 'bg-amber-200 text-amber-900 font-semibold'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {isNeedsMerge && <GitMerge className="h-3 w-3 shrink-0" />}
                            {k}: {String(v).length > 30 ? String(v).slice(0, 30) + '…' : v}
                          </span>
                        );
                      })}
                    </td>
                    <td className="px-4 py-3 align-top space-y-1">
                      {xact.postings.map((p, i) => {
                        const suspense = isSuspenseAccount(p.account);
                        return (
                          <div key={i} className="flex justify-between text-xs gap-3">
                            <span className={suspense ? 'font-semibold text-amber-700' : 'text-gray-700'}>
                              {p.account}
                            </span>
                            <span className="font-mono text-gray-700">
                              {p.amount >= 0 ? '+' : ''}{p.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} {p.commodity}
                            </span>
                          </div>
                        );
                      })}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      {editing === xact.id ? (
                        <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600" title="Cancelar">
                          <X className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => openEdit(xact)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Promover
                        </button>
                      )}
                    </td>
                  </tr>
                  {transferPartner && (
                    <tr className="bg-amber-50/60">
                      <td colSpan={5} className="px-4 py-2">
                        <div className="flex items-center flex-wrap gap-3 text-xs text-gray-700">
                          <span className="flex items-center gap-1 text-amber-800 font-semibold whitespace-nowrap">
                            <GitMerge className="h-3.5 w-3.5" />
                            Par sugerido:
                          </span>
                          <span className="text-gray-600">{transferPartner.date.replace(/\//g, '-')}</span>
                          <span className="font-medium text-gray-900">{transferPartner.payee}</span>
                          {transferPartner.tags['mcp-counterparty-name'] && (
                            <span className="text-gray-500">
                              ({transferPartner.tags['mcp-counterparty-name']})
                            </span>
                          )}
                          {(() => {
                            const bp =
                              transferPartner.postings.find((p) => !isSuspenseAccount(p.account)) ??
                              transferPartner.postings[0];
                            return bp ? (
                              <span className="font-mono text-gray-600">
                                {fmtBRL(Math.abs(bp.amount))} · {bp.account}
                              </span>
                            ) : null;
                          })()}
                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Vincular "${xact.payee}" com "${transferPartner.payee}" como transferência?`
                                )
                              ) {
                                linkPair(xact.id, transferPartner.id);
                              }
                            }}
                            disabled={linking}
                            className="ml-auto flex items-center gap-1 px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
                          >
                            <GitMerge className="h-3.5 w-3.5" />
                            Vincular com o par sugerido
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {editing === xact.id && (
                    <tr className="bg-amber-50/60">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="space-y-2">
                          {Object.keys(replacements).map((suspense) => (
                            <div key={suspense} className="flex items-center gap-2">
                              <span className="text-xs font-medium text-amber-700 w-44">{suspense}</span>
                              <span className="text-gray-400">→</span>
                              <input
                                list={`acc-list-${xact.id}`}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                                placeholder="Receitas:Serviços:..."
                                value={replacements[suspense]}
                                onChange={(e) =>
                                  setReplacements({ ...replacements, [suspense]: e.target.value })
                                }
                              />
                              <datalist id={`acc-list-${xact.id}`}>
                                {accounts.map((a) => <option key={a} value={a} />)}
                              </datalist>
                            </div>
                          ))}
                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => submitPromote(xact.id)}
                              disabled={promoting}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              <Check className="h-3.5 w-3.5" />
                              Promover
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="sticky bottom-0 z-20 bg-white border rounded-lg shadow-lg p-3 flex items-center justify-between gap-4">
          <div className="text-sm text-gray-700">
            <span className="font-medium">{selected.size}</span> selecionado(s)
            {selected.size !== 2 && (
              <span className="text-gray-400 ml-2">— selecione exatamente 2 para vincular</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Limpar
            </button>
            <button
              onClick={handleLinkTransfer}
              disabled={selected.size !== 2 || linking}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeftRight className="h-4 w-4" />
              {linking ? 'Vinculando...' : 'Vincular como transferência'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
